import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { CheckCircle, Loader } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_API_URL;

const PaymentSuccess = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  // Support both session_id (legacy) and order_id (CashFree)
  const sessionId = searchParams.get('session_id') || searchParams.get('order_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your bookings.');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPaymentStatus(response.data);

      if (response.data.payment_status === 'paid') {
        setLoading(false);
        toast.success('Payment successful!');
      } else if (response.data.status === 'expired') {
        setLoading(false);
        toast.error('Payment session expired.');
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setLoading(false);
      toast.error('Error verifying payment status');
    }
  };

  useEffect(() => {
    if (paymentStatus?.payment_status === 'paid' && paymentStatus?.order_type === 'product' && user?.id) {
      localStorage.removeItem(`wearoclub_cart_${user.id}`);
    }
  }, [paymentStatus, user?.id]);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar user={user} />

      <div className="max-w-4xl mx-auto px-4 py-24">
        <div className="glass-card p-12 text-center" data-testid="payment-success-container">
          {loading ? (
            <>
              <div className="flex justify-center mb-6">
                <Loader className="w-16 h-16 text-[#d4a574] animate-spin" />
              </div>
              <h1 className="text-3xl font-bold mb-4">Verifying Payment...</h1>
              <p className="text-gray-400">Please wait while we confirm your payment.</p>
            </>
          ) : paymentStatus?.payment_status === 'paid' ? (
            <>
              <div className="flex justify-center mb-6">
                <CheckCircle className="w-16 h-16 text-green-400" data-testid="success-icon" />
              </div>
              <h1 className="text-3xl font-bold mb-4 gradient-text" data-testid="success-title">
                Payment Successful!
              </h1>
              <p className="text-gray-400 mb-8 text-lg">
                {paymentStatus?.order_type === 'product'
                  ? 'Your order has been confirmed successfully.'
                  : 'Your booking has been confirmed. The designer will contact you shortly.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate(`/dashboard?tab=${paymentStatus?.order_type === 'product' ? 'orders' : 'bookings'}`)}
                  className="btn-primary"
                  data-testid="view-bookings-btn"
                >
                  {paymentStatus?.order_type === 'product' ? 'View My Orders' : 'View My Bookings'}
                </button>
                <button
                  onClick={() => navigate(paymentStatus?.order_type === 'product' ? '/products' : '/designers')}
                  className="btn-secondary"
                  data-testid="browse-more-btn"
                >
                  {paymentStatus?.order_type === 'product' ? 'Continue Shopping' : 'Browse More Designers'}
                </button>
              </div>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold mb-4 text-red-400">Payment Failed</h1>
              <p className="text-gray-400 mb-8">There was an issue with your payment. Please try again.</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="btn-primary"
              >
                Go to Dashboard
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
